import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebase.config';
import { useAuth } from '../AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserManagement = () => {
  const { isSuperAdmin, currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [adminTenantInfo, setAdminTenantInfo] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [userFormData, setUserFormData] = useState({
    userId: '',
    userName: '',
    userGroup: '',
    userEmailId: '',
    userMobileNo: '',
    role: 'AppUser',
  });

  const [groupFormData, setGroupFormData] = useState({
    userGrpId: '',
    userGrpName: '',
    userRole: 'AppUser',
  });

  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    // For super admin, only fetch data after a tenant is selected.
    // For regular tenant admin, fetch immediately.
    const storedTenantId = sessionStorage.getItem('selectedTenantId');
    if (isSuperAdmin) {
      if (storedTenantId) {
        setSelectedTenantId(storedTenantId);
      } else {
        setUsers([]);
        setUserGroups([]);
      }
    } else {
      // For tenant admin, no need to wait for selection
      fetchAdminTenantInfo();
    }
  }, [isSuperAdmin, currentUser]);

  // This effect runs when a tenant admin's info is loaded OR when a super admin selects a tenant.
  useEffect(() => {
    if (adminTenantInfo) {
      console.log("Admin tenant info available, fetching data...");
      fetchUsers();
      fetchUserGroups();
    }
  }, [adminTenantInfo]);

  // Fetch available tenants (for Super Admin dropdown)
  useEffect(() => {
    const fetchTenants = async () => {
      if (!isSuperAdmin) return;
      try {
        const tenantsRef = collection(firestore, "Tenants");
        const querySnapshot = await getDocs(tenantsRef);
        const tenantsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableTenants(tenantsData);

        const storedTenantId = sessionStorage.getItem('selectedTenantId');
        if (storedTenantId) {
          const selectedTenant = tenantsData.find(t => t.id === storedTenantId);
          if (selectedTenant) {
            // Set the full tenant object to trigger data fetching
            setAdminTenantInfo({ tenantId: selectedTenant.id, ...selectedTenant });
          }
        }
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    };
    fetchTenants();
  }, [isSuperAdmin]);
  
  // CRITICAL FIX: Correctly fetches tenant admin info, including the document ID.
  const fetchAdminTenantInfo = async () => {
    if (!currentUser || isSuperAdmin) return;
    try {
      const tenantsRef = collection(firestore, "Tenants");
      const tenantQuery = query(tenantsRef, where("adminEmailId", "==", currentUser.email));
      const tenantSnapshot = await getDocs(tenantQuery);

      if (!tenantSnapshot.empty) {
        const tenantDoc = tenantSnapshot.docs[0];
        const tenantData = {
          tenantId: tenantDoc.id, // Explicitly set tenantId from the doc ID
          ...tenantDoc.data()     // Spread the rest of the fields
        };
        console.log("Found tenant data (with ID):", tenantData);
        setAdminTenantInfo(tenantData);
      }
    } catch (error) {
      console.error("Error fetching admin tenant info:", error);
    }
  };


  const handleTenantChange = (event) => {
    const tenantId = event.target.value;
    setSelectedTenantId(tenantId);
    sessionStorage.setItem('selectedTenantId', tenantId);

    if (tenantId) {
      const selectedTenant = availableTenants.find(t => t.id === tenantId);
      if (selectedTenant) {
        setAdminTenantInfo({ tenantId: selectedTenant.id, ...selectedTenant });
      }
    } else {
      setAdminTenantInfo(null);
      setUsers([]);
      setUserGroups([]);
    }
  };

  const fetchUsers = async () => {
    if (!adminTenantInfo?.tenantId) {
        setUsers([]);
        return;
    }
    try {
      const usersRef = collection(firestore, "UserProfileMaster");
      const queryRef = query(usersRef, where("tenantId", "==", adminTenantInfo.tenantId));
      const querySnapshot = await getDocs(queryRef);

      const usersData = querySnapshot.docs.map(doc => ({
        userId: doc.id, // Map the document ID to userId
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error)
    {
      console.error("Error fetching users:", error);
      setErrors({ submission: "Failed to fetch users." });
    }
  };

  const fetchUserGroups = async () => {
    if (!adminTenantInfo?.tenantId) {
        setUserGroups([]);
        return;
    }
    try {
        const groupsRef = collection(firestore, "UserGroups");
        const queryRef = query(groupsRef, where("tenantId", "==", adminTenantInfo.tenantId));
        const qSnap = await getDocs(queryRef);
        const groupsData = qSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUserGroups(groupsData);
    } catch (error) {
        console.error("Error fetching user groups:", error);
        setUserGroups([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedItem(null);
    setErrors({});
    setSubmitSuccess(false);
  };

  const handleUserFormChange = (e) => {
    setUserFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateUserForm = () => {
    let tempErrors = {};
    if (!userFormData.userName) tempErrors.userName = 'User Name is required';
    if (!userFormData.userEmailId) {
      tempErrors.userEmailId = 'Email ID is required';
    } else if (!/\S+@\S+\.\S+/.test(userFormData.userEmailId)) {
      tempErrors.userEmailId = 'Invalid email format';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;
    if (!adminTenantInfo?.tenantId) {
        setErrors({ submission: "Cannot add user: Tenant not selected." });
        return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmitSuccess(false);

    try {
      const userProfileData = {
        email: userFormData.userEmailId,
        displayName: userFormData.userName,
        mobileNo: userFormData.userMobileNo || '',
        role: userFormData.role,
        status: 'active',
        tenantId: adminTenantInfo.tenantId,
        tenantName: adminTenantInfo.tenantName || '',
      };

      if (selectedItem) {
        // CRITICAL FIX: Use 'userId' which holds the document ID.
        const userDocRef = doc(firestore, "UserProfileMaster", selectedItem.userId);
        await updateDoc(userDocRef, { ...userProfileData, updatedAt: new Date().toISOString(), updatedBy: currentUser?.email || '' });
      } else {
        // When an admin creates a user, record who added them so we can later show admin-owned users' expenses.
        const addedByEmail = currentUser?.email || '';
        const addedByName = currentUser?.displayName || currentUser?.email || '';
        await addDoc(collection(firestore, "UserProfileMaster"), { ...userProfileData, createdAt: new Date().toISOString(), addedBy: addedByEmail, addedByName });
      }

      setUserFormData({ userId: '', userName: '', userGroup: '', userEmailId: '', userMobileNo: '', role: 'AppUser' });
      setSelectedItem(null);
      setSubmitSuccess(true);
      await fetchUsers();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error("Error managing user:", error);
      setErrors({ submission: "Operation failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEdit = (item) => {
    setSelectedItem(item);
    if (tabValue === 0) {
      setUserFormData({
        userId: item.userId,
        userName: item.displayName || '',
        userEmailId: item.email || '',
        userMobileNo: item.mobileNo || '',
        role: 'AppUser', // MODIFIED: Hardcoded to AppUser
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const collectionName = tabValue === 0 ? "UserProfileMaster" : "UserGroups";
        await deleteDoc(doc(firestore, collectionName, id));
        if (tabValue === 0) {
          setUsers(prev => prev.filter(user => user.userId !== id));
        } else {
          setUserGroups(prev => prev.filter(group => group.id !== id));
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        setErrors({ submission: "Failed to delete item." });
      }
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>User Management</Typography>

        {isSuperAdmin && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Tenant</InputLabel>
              <Select value={selectedTenantId} onChange={handleTenantChange} label="Select Tenant">
                <MenuItem value=""><em>Select a tenant</em></MenuItem>
                {availableTenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.tenantName} ({tenant.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}

        {(!isSuperAdmin || selectedTenantId) ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}><Tab label="User List" /></Tabs>
            </Box>

            {submitSuccess && <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#e8f5e9' }}><Typography color="success.main">{selectedItem ? 'Updated successfully!' : 'Added successfully!'}</Typography></Paper>}
            {errors.submission && <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#ffebee' }}><Typography color="error">{errors.submission}</Typography></Paper>}

            <TabPanel value={tabValue} index={0}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <form onSubmit={handleUserSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="User Name" name="userName" value={userFormData.userName} onChange={handleUserFormChange} required error={!!errors.userName} helperText={errors.userName} fullWidth />
                    </Grid>
                    {/* MODIFICATION START: Replaced role dropdown with a disabled text field */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Role"
                        name="role"
                        value="App User"
                        fullWidth
                        disabled
                      />
                    </Grid>
                    {/* MODIFICATION END */}
                    <Grid item xs={12} sm={6}>
                      <TextField label="User Email ID" name="userEmailId" value={userFormData.userEmailId} onChange={handleUserFormChange} required type="email" error={!!errors.userEmailId} helperText={errors.userEmailId} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="User Mobile No" name="userMobileNo" value={userFormData.userMobileNo} onChange={handleUserFormChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" color="primary" disabled={isSubmitting} sx={{ mr: 2 }}>{selectedItem ? 'Update User' : 'Add User'}</Button>
                      {selectedItem && (<Button variant="outlined" onClick={() => { setSelectedItem(null); setUserFormData({ userId: '', userName: '', userGroup: '', userEmailId: '', userMobileNo: '', role: 'AppUser' }); }}>Cancel Edit</Button>)}
                    </Grid>
                  </Grid>
                </form>
              </Paper>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User Name</TableCell>
                      <TableCell>User Email ID</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.displayName}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit"><IconButton color="primary" onClick={() => handleEdit(user)}><Edit /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton color="error" onClick={() => handleDelete(user.userId)}><Delete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </>
        ) : (isSuperAdmin && !selectedTenantId &&
          <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#fff3e0' }}>
            <Typography color="warning.main">Please select a tenant to manage users.</Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default UserManagement;